/**
 * The ONE query-key factory (rules/01 §3). Every tenant-scoped key starts with ["tenant", tenantId]
 * so a tenant switch or logout can drop everything at once and rows never leak between organisations.
 * A guard test asserts that every factory below `tenant` embeds the id.
 */

/** M24 (contracts §10 S1): the `?kind=` of `GET /devices`; the api's default is `terminal`. */
export type DeviceListKind = "terminal" | "face_kiosk" | "all";

export interface EventListFilters {
  from: string;
  to: string;
  deviceId?: string;
  result?: "granted" | "denied";
  /** Unlock-method codes, comma-separated ("15,17"); rows carry the name. */
  method?: string;
  personId?: string;
  door?: number;
  /** M24: `face_kiosk` — a receipt promoted from a Face Kiosk phone. */
  source?: "push" | "backfill" | "face_kiosk";
  /** M24 (C2-13): merge the uncounted Face Kiosk receipts in (the Events page only; never the export). */
  includeUncounted?: boolean;
  limit?: number;
}

export interface EventSummaryFilters {
  from: string;
  to: string;
  deviceId?: string;
}

export interface PeopleListFilters {
  q?: string;
  status?: "active" | "disabled";
  /**
   * M13 §3: the list narrowed to one terminal, and to who effectively reaches it. `access` is only
   * meaningful beside a `deviceId` — "everyone who is on" is not a question without a door — and both
   * are part of the key, so the filtered list is its own cache entry rather than a filtered copy of
   * the unfiltered one.
   */
  deviceId?: string;
  access?: "on" | "off";
  groupId?: string;
  limit: number;
}

/** Who is on one terminal (M13 §3): searched and access-filtered; the cursor lives in the infinite query. */
export interface DevicePeopleFilters {
  q?: string;
  access?: "on" | "off";
  limit: number;
}

/**
 * Members of one access group (M13 §3): searched like the people list and keyset-paged, with the
 * cursor inside the infinite query rather than here, so two readers of the same group share a cache entry.
 */
export interface GroupMembersFilters {
  q?: string;
  limit: number;
}

/** One group's grant history (M14 §2, ADR-0018 decision 10): filterable by person, source and state. */
export interface AccessGroupGrantsFilters {
  personId?: string;
  source?: string;
  state?: string;
  limit: number;
}

/**
 * Attendance (M9 §4): organisation **days**, not instants — `from`/`to` are bare `YYYY-MM-DD` in the
 * organisation's zone. Keyset pages (`before` + `beforeId`) live inside the infinite query, not here.
 */
export interface AttendanceFilters {
  from: string;
  to: string;
  personId?: string;
  deviceId?: string;
}

/**
 * Daily muster (M12 §1): ONE organisation day, so there is no range at all. The keyset page lives in the
 * infinite query as an opaque `cursor`, never here — two readers on the same day share one cache entry.
 */
export interface MusterFilters {
  day: string;
  deviceId?: string;
  status?: "present" | "absent";
}

/** Terminal activity (M12 §1): organisation days, at most 93 apart. `before`/`beforeId` page inside the query. */
export interface TerminalActivityFilters {
  from: string;
  to: string;
  deviceId?: string;
}

/** Denied access (M12 §1): organisation days, at most 31 apart. One answer, capped by the api — no paging. */
export interface DeniedReportFilters {
  from: string;
  to: string;
  deviceId?: string;
  personId?: string;
}

/** `GET /ledger/outstanding` and its report sibling: page/size, never a cursor (both cap at 200 a page). */
export interface LedgerOutstandingFilters {
  page: number;
  size: number;
  search?: string;
  filter?: "owing" | "credit" | "nonzero";
  sort?: string;
  dir?: "asc" | "desc";
  includeArchived?: "true" | "false";
}

/**
 * The gym's member directory (M15, `@nacs/shared` `gymMembersQuery`): page/size like the dues list,
 * searched by name, member number or the person's own reference, and narrowed by the DERIVED
 * membership state rather than a stored one.
 */
/**
 * M16 wave 2: the visitors list is keyset-paged (`cursor`), so the FILTERS are the key and the cursor
 * is not — an infinite query owns its own pages, and putting the cursor in the key would make every
 * "load more" a different cache entry.
 */
export interface VisitorsFilters {
  search?: string;
  hostPersonId?: string;
}

export interface VisitsFilters {
  visitorId?: string;
  state?: string;
  open?: boolean;
}

/**
 * `GET /gym/registrations/lookup` (2026-09-21): what the desk has typed so far, debounced. The three
 * are the KEY rather than one search string because the api compares them differently — a mobile
 * exactly, a name as a contains — and one blob would make the cache lie about which was asked.
 */
export interface GymRegistrationLookupFilters {
  name?: string;
  mobile?: string;
  email?: string;
}

export interface GymMembersFilters {
  page: number;
  size: number;
  search?: string;
  status?: string;
  state?: string;
  /** Whose contract ends within N organisation days — the dashboard's tiles, followed as a list. */
  expiringWithinDays?: number;
  planId?: string;
  trainerPersonId?: string;
  sort?: string;
  dir?: "asc" | "desc";
}

/**
 * M17: the gym's lead list. Page/size like the member directory beside it, and every narrowing the
 * api takes is part of the key — including `dueBefore`, which is an ORGANISATION day (ADR-0003) and
 * therefore changes at midnight in the tenant's zone rather than the reader's: the default list
 * ("due today and overdue") is a different cache entry tomorrow, which is exactly right.
 *
 * `open` and `unassigned` are the api's "true"/"false" strings rather than booleans, because that
 * is what goes on the wire — `z.coerce.boolean()` would make the string "false" true, which is the
 * bug `gymDuesReportQuery.includeInactive` records.
 */
export interface GymLeadsFilters {
  page: number;
  size: number;
  q?: string;
  status?: string;
  open?: "true" | "false";
  ownerUserId?: string;
  unassigned?: "true" | "false";
  source?: string;
  dueBefore?: string;
  sort?: string;
  dir?: "asc" | "desc";
}

/** Memberships & expiry (M15 §1 row 10.2): page/size, organisation days over the CONTRACTUAL end date. */
export interface GymMembershipsReportFilters {
  page: number;
  size: number;
  from?: string;
  to?: string;
  state?: string;
  planId?: string;
  memberStatus?: string;
  sort?: string;
  dir?: "asc" | "desc";
}

/** Dues (M15 §1 row 10.2): page/size, like the ledger's own Outstanding balances report. */
export interface GymDuesReportFilters {
  page: number;
  size: number;
  filter?: "owing" | "credit" | "nonzero";
  includeInactive?: "true" | "false";
  sort?: string;
  dir?: "asc" | "desc";
}

/**
 * Leads & conversions (M17 plan §6.4): page/size over organisation days on the CAPTURE day — when
 * the enquiry came in, never when it closed, so "of the enquiries we took in March, how many
 * joined" keeps its answer as people convert late.
 */
export interface GymLeadsReportFilters {
  page: number;
  size: number;
  from?: string;
  to?: string;
  status?: string;
  source?: string;
  ownerUserId?: string;
  sort?: string;
  dir?: "asc" | "desc";
}

/**
 * M22 (plan §4): the gym's attendance reports, every one over organisation DAYS (`from`/`to`, at most
 * 366 apart; omitted = the api's default window). Visits and Peak hours answer in one read; Member
 * attendance and Inactive members page by page/size like the memberships report.
 */
export interface GymVisitsReportFilters {
  from?: string;
  to?: string;
  groupBy?: "day" | "week" | "month";
}

export interface GymAttendanceReportFilters {
  page: number;
  size: number;
  from?: string;
  to?: string;
  /** A gym member's PERSON id — the member page's key (D14). */
  personId?: string;
  sort?: "day" | "name" | "firstIn";
  dir?: "asc" | "desc";
}

export interface GymInactiveReportFilters {
  page: number;
  size: number;
  /** Not seen for this many days (7–365; the dashboard's tile is 30). */
  days?: number;
  show?: "all" | "lapsed" | "never";
  sort?: "lastVisit" | "name" | "endsOn";
  dir?: "asc" | "desc";
}

export interface GymPeakHoursReportFilters {
  from?: string;
  to?: string;
}

/** The till reconciliation (M14 §4 wave 3b): organisation days, at most 366 apart, one method or all. */
export interface LedgerPaymentsReportFilters {
  from: string;
  to: string;
  method?: string;
}

/** One person's statement: the whole history by default, `from`/`to` sent together when narrowed. */
export interface LedgerStatementFilters {
  personId: string;
  from?: string;
  to?: string;
}

/** Receipts issued: the same range bound as the till report, plus who and how. */
export interface LedgerReceiptsFilters {
  from: string;
  to: string;
  personId?: string;
  method?: string;
}

/** Reversals and adjustments: the range, who, who did it, and which of the four corrections. */
export interface LedgerCorrectionsFilters {
  from: string;
  to: string;
  personId?: string;
  operatorUserId?: string;
  kind?: string;
}

/** What the terminal is asked to look through: instants (the terminal's own records), bounded to 31 days. */
export interface TerminalEventFilters {
  from: string;
  to: string;
  userId?: string;
}

/** Audit trail filter: one action or all; pages are keyed by `before` inside the infinite query, not here. */
export interface AuditFilters {
  action?: string;
  limit: number;
}

/** Invoices are a small master list, so the API pages them by page/size rather than a cursor (M7). */
export interface InvoiceFilters {
  page: number;
  size: number;
}

/** Delivery log filter: one status or all; keyset pages (`before`+`beforeId`) live inside the infinite query. */
export interface DeliveryFilters {
  status?: string;
  limit: number;
}

/** A configuration being priced: the estimate is keyed by it, so every distinct choice is cached once. */
export interface EstimateInput {
  period: "monthly" | "yearly";
  items: readonly { code: string; quantity: number }[];
  /**
   * What is being priced (`POST /catalogue/estimate`'s `context`): a purchase — the api's default, and
   * where a first-purchase offer lives — or a renewal, priced under the renewal-scope rules the
   * comparison's `atRenewal` column uses. Two different answers, so it is part of the key.
   */
  context?: { orderKind: "purchase" | "renewal" };
}

export const queryKeys = {
  session: {
    me: () => ["session", "me"] as const,
  },
  /**
   * The catalogue and its estimates (M11, ADR-0015). Not under `tenant` because both routes are callable
   * with no organisation selected — the signup screen prices a configuration before one exists — but still
   * keyed by the tenant id (null at signup), because an organisation's own price overrides and discounts
   * apply when it has one, and those must never be served to the next organisation from the cache.
   */
  catalogue: {
    all: (tenantId: string | null) => ["catalogue", tenantId] as const,
    list: (tenantId: string | null) => ["catalogue", tenantId, "list"] as const,
    /** Every estimate priced for this organisation: what a change to the items it holds marks stale. */
    estimates: (tenantId: string | null) => ["catalogue", tenantId, "estimate"] as const,
    estimate: (tenantId: string | null, input: EstimateInput) =>
      ["catalogue", tenantId, "estimate", input] as const,
  },
  tenant: {
    all: (tenantId: string) => ["tenant", tenantId] as const,
    devices: {
      all: (tenantId: string) => ["tenant", tenantId, "devices"] as const,
      /**
       * M24 (contracts §10 S1): `GET /devices?kind=` — the kind is in the key, so a `terminal` list and
       * an `all` list (seats included, a different row set) never share a cache entry. Omitted = the
       * prefix of every kind: what an invalidation names (presence, pairing) so no kind is left stale.
       */
      list: (tenantId: string, kind?: DeviceListKind) =>
        kind === undefined
          ? (["tenant", tenantId, "devices", "list"] as const)
          : (["tenant", tenantId, "devices", "list", kind] as const),
      detail: (tenantId: string, deviceId: string) =>
        ["tenant", tenantId, "devices", "detail", deviceId] as const,
      provisioningLog: (tenantId: string, deviceId: string) =>
        ["tenant", tenantId, "devices", "provisioning-log", deviceId] as const,
      pairingInfo: (tenantId: string) => ["tenant", tenantId, "devices", "pairing-info"] as const,
      unclaimed: (tenantId: string, serial: string) =>
        ["tenant", tenantId, "devices", "unclaimed", serial] as const,
      /** People sync counts and failures on one terminal (M3). */
      sync: (tenantId: string, deviceId: string) =>
        ["tenant", tenantId, "devices", "sync", deviceId] as const,
      /** M14 §5 (ADR-0007 addendum): how many people this terminal can hold, and how many it holds now. */
      capacity: (tenantId: string, deviceId: string) =>
        ["tenant", tenantId, "devices", "capacity", deviceId] as const,
      /**
       * M13 §3: who this terminal holds and which groups name it. Under `devices` rather than `people`
       * because they are read on the device page and every write that moves them already invalidates
       * the `devices` scope — one place to drop, one place to refetch.
       */
      people: (tenantId: string, deviceId: string, filters: DevicePeopleFilters) =>
        ["tenant", tenantId, "devices", "people", deviceId, filters] as const,
      groups: (tenantId: string, deviceId: string) =>
        ["tenant", tenantId, "devices", "groups", deviceId] as const,
      /**
       * M9 §6 terminal tools. These ask the **terminal**, not our copy of it: each one makes the device
       * do work and they are limited to ten a minute, so nothing here is polled or refetched on focus.
       */
      toolsTime: (tenantId: string, deviceId: string) =>
        ["tenant", tenantId, "devices", "tools", deviceId, "time"] as const,
      toolsEvents: (tenantId: string, deviceId: string, filters: TerminalEventFilters) =>
        ["tenant", tenantId, "devices", "tools", deviceId, "events", filters] as const,
      toolsUsers: (tenantId: string, deviceId: string) =>
        ["tenant", tenantId, "devices", "tools", deviceId, "users"] as const,
      /** The terminal's own operation log for one window; keyed by the window like `toolsEvents`. */
      toolsLog: (tenantId: string, deviceId: string, filters: TerminalEventFilters) =>
        ["tenant", tenantId, "devices", "tools", deviceId, "log", filters] as const,
    },
    people: {
      all: (tenantId: string) => ["tenant", tenantId, "people"] as const,
      list: (tenantId: string, filters: PeopleListFilters) =>
        ["tenant", tenantId, "people", "list", filters] as const,
      detail: (tenantId: string, personId: string) =>
        ["tenant", tenantId, "people", "detail", personId] as const,
      syncLog: (tenantId: string, personId: string) =>
        ["tenant", tenantId, "people", "sync-log", personId] as const,
      /** M14 §3 (ADR-0018 decision 10): "why does this person have access?" on one terminal. */
      explain: (tenantId: string, personId: string, deviceId: string) =>
        ["tenant", tenantId, "people", "explain", personId, deviceId] as const,
      /**
       * M24 (contracts §10 S2 routes 2–3): the face consent and photo state, and the consent history.
       * Under `people` so a photo write (which invalidates the scope) refreshes the tick with the photo.
       */
      faceStatus: (tenantId: string, personId: string) =>
        ["tenant", tenantId, "people", "face-status", personId] as const,
      biometricConsents: (tenantId: string, personId: string) =>
        ["tenant", tenantId, "people", "biometric-consents", personId] as const,
    },
    /**
     * Access groups (M13, ADR-0016). Their own scope rather than a corner of `people`, because a group
     * change moves people AND terminals: a mutation invalidates all three through `invalidateTenantScopes`.
     */
    accessGroups: {
      all: (tenantId: string) => ["tenant", tenantId, "access-groups"] as const,
      list: (tenantId: string) => ["tenant", tenantId, "access-groups", "list"] as const,
      detail: (tenantId: string, groupId: string) =>
        ["tenant", tenantId, "access-groups", "detail", groupId] as const,
      members: (tenantId: string, groupId: string, filters: GroupMembersFilters) =>
        ["tenant", tenantId, "access-groups", "members", groupId, filters] as const,
      /** M14 §2 (ADR-0018 decision 10): every grant of one group — active, pending, expired, revoked. */
      grants: (tenantId: string, groupId: string, filters: AccessGroupGrantsFilters) =>
        ["tenant", tenantId, "access-groups", "grants", groupId, filters] as const,
    },
    doors: {
      all: (tenantId: string) => ["tenant", tenantId, "doors"] as const,
      list: (tenantId: string) => ["tenant", tenantId, "doors", "list"] as const,
    },
    /**
     * The member ledger (M14 §4, ADR-0019): the organisation charging its OWN people. Its own scope,
     * not a corner of `people` or `billing` — a write here never touches a person's own row and must
     * never be confused with `billing`, which is Nexborg charging the organisation.
     */
    ledger: {
      all: (tenantId: string) => ["tenant", tenantId, "ledger"] as const,
      account: (tenantId: string, personId: string) =>
        ["tenant", tenantId, "ledger", "account", personId] as const,
      outstanding: (tenantId: string, filters: LedgerOutstandingFilters) =>
        ["tenant", tenantId, "ledger", "outstanding", filters] as const,
    },
    /**
     * The Gym module (M15, ADR-0017). Its own scope, like every other module's will be: a module's
     * rows are invalidated together and never through `people`, because the person is shared and the
     * relationship is not.
     *
     * `person` is keyed on the PERSON and not on a gym member id, which is the whole point: the same
     * key answers "what is this person to the gym" for the person page's link out AND for the
     * module's own member page, and it holds a LIST of relationships, so a gym client who is also a
     * gym trainer is one cache entry rather than two pages arguing over one human.
     */
    gym: {
      all: (tenantId: string) => ["tenant", tenantId, "gym"] as const,
      settings: (tenantId: string) => ["tenant", tenantId, "gym", "settings"] as const,
      plans: (tenantId: string, includeArchived: boolean) =>
        ["tenant", tenantId, "gym", "plans", includeArchived] as const,
      dashboard: (tenantId: string) => ["tenant", tenantId, "gym", "dashboard"] as const,
      trainers: (tenantId: string) => ["tenant", tenantId, "gym", "trainers"] as const,
      /**
       * The door sets the gym OFFERS its trainers (2026-09-21). Keyed on `includeArchived` like the
       * plans list beside it, because the settings screen can show the archived ones and the
       * Add-a-trainer dialog must never offer them.
       */
      trainerGroups: (tenantId: string, includeArchived: boolean) =>
        ["tenant", tenantId, "gym", "trainer-groups", includeArchived] as const,
      /**
       * 2026-09-22: the doors one MEMBER reaches — their plan's group and the extras the gym gave
       * them. Keyed on the gym member id rather than the person id, because it is the member's
       * membership the extras hang off and the member id is what the route takes.
       */
      memberAccess: (tenantId: string, memberId: string) =>
        ["tenant", tenantId, "gym", "member-access", memberId] as const,
      /**
       * The DOORS one trainer currently reaches through the gym's own grants — what the roster's
       * `accessGroups` only names. Read before a removal so the confirmation can say which doors
       * close, beside the members it names; and read by the "Change access" dialog.
       */
      trainerAccess: (tenantId: string, personId: string) =>
        ["tenant", tenantId, "gym", "trainer-access", personId] as const,
      /**
       * The members ONE trainer currently looks after, by name — what the roster's `memberCount`
       * only counts. Read before a removal so the confirmation can say who it is about to leave
       * without a trainer, which is what stops that act being a silent one.
       */
      trainerMembers: (tenantId: string, personId: string) =>
        ["tenant", tenantId, "gym", "trainer-members", personId] as const,
      /**
       * **One member's trainer HISTORY** (2026-09-21) — who has her now, who had her before, and by
       * implication the gaps between. Its own key beside `member` rather than a field on it: the
       * member card is read on every gym screen and this list is read on one, and folding a growing
       * history into the card would make every one of them pay for it.
       */
      memberTrainers: (tenantId: string, memberId: string) =>
        ["tenant", tenantId, "gym", "member-trainers", memberId] as const,
      members: (tenantId: string, filters: GymMembersFilters) =>
        ["tenant", tenantId, "gym", "members", filters] as const,
      /** The Memberships & expiry report (M15 §1 row 10.2) — the module's own, not the core hub's. */
      membershipsReport: (tenantId: string, filters: GymMembershipsReportFilters) =>
        ["tenant", tenantId, "gym", "reports", "memberships", filters] as const,
      /** Dues (M15 §1 row 10.2) — `gym:view` reads it; exporting it additionally needs `ledger:view`. */
      duesReport: (tenantId: string, filters: GymDuesReportFilters) =>
        ["tenant", tenantId, "gym", "reports", "dues", filters] as const,
      /** Leads & conversions (M17 plan §6.4) — the module's own third report, on its own hub. */
      leadsReport: (tenantId: string, filters: GymLeadsReportFilters) =>
        ["tenant", tenantId, "gym", "reports", "leads", filters] as const,
      /** M22 §6: the four attendance reports, all read from the `attendance_daily` rollup. */
      visitsReport: (tenantId: string, filters: GymVisitsReportFilters) =>
        ["tenant", tenantId, "gym", "reports", "visits", filters] as const,
      attendanceReport: (tenantId: string, filters: GymAttendanceReportFilters) =>
        ["tenant", tenantId, "gym", "reports", "attendance", filters] as const,
      inactiveReport: (tenantId: string, filters: GymInactiveReportFilters) =>
        ["tenant", tenantId, "gym", "reports", "inactive", filters] as const,
      peakHoursReport: (tenantId: string, filters: GymPeakHoursReportFilters) =>
        ["tenant", tenantId, "gym", "reports", "peak-hours", filters] as const,
      /** One member's full card, by the gym's OWN row id (not the person id). */
      member: (tenantId: string, memberId: string) =>
        ["tenant", tenantId, "gym", "member", memberId] as const,
      /**
       * "What is this person to the gym", by the shared `people.id` — resolved client-side today by
       * searching `members` and `trainers` (see `useGymPersonQuery`'s own comment) until
       * `GET /gym/people/{personId}` ships; its own key so the resolution is cached once per person
       * rather than repeated on every render of the member page.
       *
       * `personName` rides in the key (not just `enabled`) so the query re-runs the moment the name
       * arrives from the sibling `usePersonQuery` — it starts as `undefined` while that is still
       * pending, the fallback's search needs the resolved name, and gating on `enabled` instead would
       * serialise the two queries (gym waiting on person) rather than letting them run in parallel.
       */
      personRelationships: (tenantId: string, personId: string, personName: string | undefined) =>
        ["tenant", tenantId, "gym", "person-relationships", personId, personName] as const,
      /**
       * "Who might this already be?" — the register dialog's as-you-type duplicate lookup. Its own
       * key, under `gym`, so a registration invalidates it along with everything else the act moved:
       * a desk that has just created somebody must not be told a moment later that they do not
       * exist.
       */
      registrationLookup: (tenantId: string, filters: GymRegistrationLookupFilters) =>
        ["tenant", tenantId, "gym", "registration-lookup", filters] as const,
      /**
       * M17 leads. Under `gym` rather than a scope of their own, because every write that moves a
       * lead moves the gym's dashboard tiles too and a converted lead moves the member directory —
       * one scope to invalidate, one place that cannot be forgotten. (Converting additionally
       * invalidates `people`: it creates a durable, metered human.)
       */
      leads: (tenantId: string, filters: GymLeadsFilters) =>
        ["tenant", tenantId, "gym", "leads", filters] as const,
      /** One lead and its whole activity log — the drawer's own entry, keyed on the lead. */
      lead: (tenantId: string, leadId: string) => ["tenant", tenantId, "gym", "lead", leadId] as const,
    },
    /**
     * M19 Staff attendance (contracts §11). Its own scope like every module's, but **generic, keyed by
     * area**, and that is deliberate: five portal lanes fill the Staff workspace at once, and a scope
     * spelled out key by key here would have all five editing this one file. The areas are named — and
     * typed — once, in `src/modules/staff/shared/useStaffQueryKeys.ts`, which binds the tenant; a lane
     * writes `queryKey: queryKeys.list("shifts", filters)` with the bound set and never touches this file.
     *
     * Every write that moves a staff row invalidates the whole scope (`invalidateTenantScopes(…,
     * ["staff"])`): the daily engine recomputes days from shifts, holidays, requests and punches alike,
     * so there is almost no staff write whose effects stay inside one area.
     */
    staff: {
      all: (tenantId: string) => ["tenant", tenantId, "staff"] as const,
      /** Everything one area holds ("shifts", "days", "approvals") — the unit a narrower invalidation names. */
      area: (tenantId: string, area: string) => ["tenant", tenantId, "staff", area] as const,
      /** One list read, keyed by everything it was asked with — filters only, never a cursor. */
      list: (tenantId: string, area: string, filters: object = {}) =>
        ["tenant", tenantId, "staff", area, "list", filters] as const,
      /** One row read, by its own key: a person id, a request id, a month. */
      detail: (tenantId: string, area: string, id: string) =>
        ["tenant", tenantId, "staff", area, "detail", id] as const,
    },
    /**
     * M20 Student Attendance (contracts §11): generic and keyed by area, for the reason `staff` is — six
     * portal lanes fill the workspace at once. The areas are named and typed once, in
     * `src/modules/students/shared/useStudentsQueryKeys.ts`. Every students write drops the whole scope.
     */
    students: {
      all: (tenantId: string) => ["tenant", tenantId, "students"] as const,
      area: (tenantId: string, area: string) => ["tenant", tenantId, "students", area] as const,
      list: (tenantId: string, area: string, filters: object = {}) =>
        ["tenant", tenantId, "students", area, "list", filters] as const,
      detail: (tenantId: string, area: string, id: string) =>
        ["tenant", tenantId, "students", area, "detail", id] as const,
    },
    /**
     * M24 Face Kiosk (contracts §11, ADR-0026): generic and keyed by area, for the reason `staff` and
     * `students` are. The areas are named and typed once, in `src/modules/faceKiosk/shared/useFaceKioskQueryKeys.ts`.
     * Every Face Kiosk write drops the whole scope; a seat write names `devices` beside it (a seat is a slot).
     */
    faceKiosk: {
      all: (tenantId: string) => ["tenant", tenantId, "faceKiosk"] as const,
      area: (tenantId: string, area: string) => ["tenant", tenantId, "faceKiosk", area] as const,
      list: (tenantId: string, area: string, filters: object = {}) =>
        ["tenant", tenantId, "faceKiosk", area, "list", filters] as const,
      detail: (tenantId: string, area: string, id: string) =>
        ["tenant", tenantId, "faceKiosk", area, "detail", id] as const,
    },
    /**
     * M25 Labour attendance (contracts §15, ADR-0027): generic and keyed by area, for the reason `staff` and
     * `students` are — six portal lanes fill the workspace at once. The areas are named and typed once, in
     * `src/modules/labour/shared/useLabourQueryKeys.ts`. Every labour write drops the whole scope (a day is
     * computed from assignments, settings, holidays, corrections and punches alike).
     */
    labour: {
      all: (tenantId: string) => ["tenant", tenantId, "labour"] as const,
      area: (tenantId: string, area: string) => ["tenant", tenantId, "labour", area] as const,
      list: (tenantId: string, area: string, filters: object = {}) =>
        ["tenant", tenantId, "labour", area, "list", filters] as const,
      detail: (tenantId: string, area: string, id: string) =>
        ["tenant", tenantId, "labour", area, "detail", id] as const,
    },
    /**
     * M21 Co-working (contracts §15, ADR-0028/0029): generic and keyed by area, for the reason `labour` is — eight
     * portal lanes fill the workspace at once. The areas are named and typed once, in
     * `src/modules/coworking/shared/useCwkQueryKeys.ts`. Every co-working write drops the whole scope (a
     * balance, a block and a seat's door move together); the ledger reads the module makes live under it too, so
     * its `402 ADDON_REQUIRED` reaches the workspace gate.
     */
    coworking: {
      all: (tenantId: string) => ["tenant", tenantId, "coworking"] as const,
      area: (tenantId: string, area: string) => ["tenant", tenantId, "coworking", area] as const,
      list: (tenantId: string, area: string, filters: object = {}) =>
        ["tenant", tenantId, "coworking", area, "list", filters] as const,
      detail: (tenantId: string, area: string, id: string) =>
        ["tenant", tenantId, "coworking", area, "detail", id] as const,
    },
    /**
     * M27 Rooms and Equipment (contracts §18, ADR-0030 addendum 2026-10-28): generic and keyed by area, for the reason
     * `coworking` is — four portal lanes fill the workspace at once. The areas are named and typed once, in
     * `src/modules/rooms/shared/roomsQueryKeys.ts`. A booking write drops the whole scope (a calendar, a count and
     * a readiness chip move together); `rooms.booking_changed` on the stream drops only the areas it can touch.
     */
    rooms: {
      all: (tenantId: string) => ["tenant", tenantId, "rooms"] as const,
      area: (tenantId: string, area: string) => ["tenant", tenantId, "rooms", area] as const,
      list: (tenantId: string, area: string, filters: object = {}) =>
        ["tenant", tenantId, "rooms", area, "list", filters] as const,
      detail: (tenantId: string, area: string, id: string) =>
        ["tenant", tenantId, "rooms", area, "detail", id] as const,
    },
    /**
     * M26 Payroll (contracts §20, ADR-0031): generic and keyed by area, for the reason `staff` and `labour` are —
     * six portal lanes fill the workspace at once. The areas are named and typed once, in
     * `src/modules/payroll/shared/usePayrollQueryKeys.ts`. A revealed PAN, UAN or account number is NEVER cached
     * under any of these keys: it lives in the revealing component's state only (contracts §20, custody).
     */
    payroll: {
      all: (tenantId: string) => ["tenant", tenantId, "payroll"] as const,
      area: (tenantId: string, area: string) => ["tenant", tenantId, "payroll", area] as const,
      list: (tenantId: string, area: string, filters: object = {}) =>
        ["tenant", tenantId, "payroll", area, "list", filters] as const,
      detail: (tenantId: string, area: string, id: string) =>
        ["tenant", tenantId, "payroll", area, "detail", id] as const,
    },
    /**
     * M20 (ADR-0024): the organisation's shared SMS balance — core, so a scope of its own rather than a
     * corner of `students`. Same generic shape; a recharge or a students SMS write names it beside its own.
     */
    sms: {
      all: (tenantId: string) => ["tenant", tenantId, "sms"] as const,
      area: (tenantId: string, area: string) => ["tenant", tenantId, "sms", area] as const,
      list: (tenantId: string, area: string, filters: object = {}) =>
        ["tenant", tenantId, "sms", area, "list", filters] as const,
      detail: (tenantId: string, area: string, id: string) =>
        ["tenant", tenantId, "sms", area, "detail", id] as const,
    },
    /**
     * Documents kept on a module profile (plans/v2/attachments/README.md §6) — an employee's, a gym
     * member's, a trainer's. One list per (scope, owner), keyed by the id the route takes: the gym
     * MEMBER id for `gym_member`, the person id for the other two, so a person who is a member and a
     * trainer is two entries with two allowances, never one list arguing over both.
     *
     * Its own scope rather than a corner of `staff` or `gym`, because one card serves both modules and
     * a document moves nothing else: an upload or a delete invalidates `attachments` and nothing more,
     * while a lifecycle write that starts or cancels the deletion clock names `attachments` beside its
     * own scope.
     */
    attachments: {
      all: (tenantId: string) => ["tenant", tenantId, "attachments"] as const,
      list: (tenantId: string, scope: string, ownerId: string) =>
        ["tenant", tenantId, "attachments", "list", scope, ownerId] as const,
    },
    /**
     * "What is this person to module X?" — `GET /<module>/people/{personId}`, one entry per module,
     * keyed by the module id and the PERSON (ADR-0017 second addendum). Its own scope rather than a
     * corner of `people` or of `gym`, because it is the one key TWO readers share: the core person
     * page's line of links out, and the module's own page about that person. One cache entry, one
     * request, and no way for the two directions of the relationship to disagree on screen.
     */
    modules: {
      all: (tenantId: string) => ["tenant", tenantId, "modules"] as const,
      person: (tenantId: string, moduleId: string, personId: string) =>
        ["tenant", tenantId, "modules", moduleId, "person", personId] as const,
    },
    events: {
      all: (tenantId: string) => ["tenant", tenantId, "events"] as const,
      list: (tenantId: string, filters: EventListFilters) =>
        ["tenant", tenantId, "events", "list", filters] as const,
      /** The newest page polled for the "N new events" pill; separate from the infinite list so it never resets it. */
      latest: (tenantId: string, filters: EventListFilters) =>
        ["tenant", tenantId, "events", "latest", filters] as const,
      detail: (tenantId: string, eventId: string) =>
        ["tenant", tenantId, "events", "detail", eventId] as const,
      summary: (tenantId: string, filters: EventSummaryFilters) =>
        ["tenant", tenantId, "events", "summary", filters] as const,
    },
    /**
     * M9 §4 attendance and the M12 §1 reports built on the same rollups: every one of them reads the
     * worker's daily tables rather than the event log, except `denied`, which is the only report that
     * has to (no rollup carries the terminal's reason code).
     */
    reports: {
      all: (tenantId: string) => ["tenant", tenantId, "reports"] as const,
      attendance: (tenantId: string, filters: AttendanceFilters) =>
        ["tenant", tenantId, "reports", "attendance", filters] as const,
      attendanceSummary: (tenantId: string, filters: AttendanceFilters) =>
        ["tenant", tenantId, "reports", "attendance-summary", filters] as const,
      muster: (tenantId: string, filters: MusterFilters) =>
        ["tenant", tenantId, "reports", "muster", filters] as const,
      terminals: (tenantId: string, filters: TerminalActivityFilters) =>
        ["tenant", tenantId, "reports", "terminals", filters] as const,
      denied: (tenantId: string, filters: DeniedReportFilters) =>
        ["tenant", tenantId, "reports", "denied", filters] as const,
      /** M14 §4 wave 3b (ADR-0019 decision 9): the five ledger reports, guarded `ledger:view`. */
      ledgerOutstanding: (tenantId: string, filters: LedgerOutstandingFilters) =>
        ["tenant", tenantId, "reports", "ledger-outstanding", filters] as const,
      ledgerPayments: (tenantId: string, filters: LedgerPaymentsReportFilters) =>
        ["tenant", tenantId, "reports", "ledger-payments", filters] as const,
      ledgerStatement: (tenantId: string, filters: LedgerStatementFilters) =>
        ["tenant", tenantId, "reports", "ledger-statement", filters] as const,
      ledgerReceipts: (tenantId: string, filters: LedgerReceiptsFilters) =>
        ["tenant", tenantId, "reports", "ledger-receipts", filters] as const,
      ledgerCorrections: (tenantId: string, filters: LedgerCorrectionsFilters) =>
        ["tenant", tenantId, "reports", "ledger-corrections", filters] as const,
    },
    /**
     * M16 wave 2 (ADR-0021 decision 9): the Visitors module. TWO lists and two details, because the
     * durable visitor and the visit have different lifetimes and a page of one must not be
     * invalidated by a write to the other — checking somebody out changes a visit and nothing about
     * the person who visited.
     */
    visitors: {
      all: (tenantId: string) => ["tenant", tenantId, "visitors"] as const,
      list: (tenantId: string, filters: VisitorsFilters) =>
        ["tenant", tenantId, "visitors", "list", filters] as const,
      detail: (tenantId: string, visitorId: string) =>
        ["tenant", tenantId, "visitors", "detail", visitorId] as const,
      visits: (tenantId: string, filters: VisitsFilters) =>
        ["tenant", tenantId, "visitors", "visits", filters] as const,
      punches: (tenantId: string, visitId: string) =>
        ["tenant", tenantId, "visitors", "punches", visitId] as const,
      /** Visitors → Settings (pass links, 2026-10-18): the four switches, and what one link SMS costs today. */
      settings: (tenantId: string) => ["tenant", tenantId, "visitors", "settings"] as const,
    },
    /**
     * M16 wave 4: the pass primitive, read one at a time. The live scan view (`/visitors/scans`) has
     * a stream message carrying ids and looks the pass up through the ordinary API so the person's
     * name, the group and "3 of 5 used" come through this role's own permissions rather than over the
     * wire — which is what keeps the rule "a message is worthless to anyone who cannot already read
     * the row it names" true of a scan too.
     *
     * Its own scope rather than a corner of `visitors`, because a pass is a shared primitive: a gym
     * day pass and a meeting-room guest write the same row, and neither is a visit.
     */
    passes: {
      all: (tenantId: string) => ["tenant", tenantId, "passes"] as const,
      detail: (tenantId: string, passId: string) => ["tenant", tenantId, "passes", "detail", passId] as const,
      /**
       * The live-scans backfill: the last few minutes off the stream's own replay buffer, read once
       * when the page mounts (`staleTime: Infinity`, no focus refetch — the SOCKET is what keeps the
       * page current, and a page that polled this would be a second, worse stream).
       *
       * It sits under the `passes` prefix, so an `all` invalidation does reach it. That is harmless
       * rather than wrong: the page merges the buffer with the live messages by envelope id, so a
       * refetch re-adds nothing it is already showing — and `pass.scan` itself invalidates only
       * `detail` and the visitors tree, never this.
       */
      recentScans: (tenantId: string) => ["tenant", tenantId, "passes", "recent-scans"] as const,
    },
    /** M9 §5: weekly templates and holiday lists. Small master lists, read whole. */
    schedules: {
      all: (tenantId: string) => ["tenant", tenantId, "schedules"] as const,
      list: (tenantId: string) => ["tenant", tenantId, "schedules", "list"] as const,
      holidayGroups: (tenantId: string) => ["tenant", tenantId, "schedules", "holiday-groups"] as const,
    },
    members: {
      all: (tenantId: string) => ["tenant", tenantId, "members"] as const,
      list: (tenantId: string) => ["tenant", tenantId, "members", "list"] as const,
      /** ADR-0023 §7: the employee-only memberships (`kind=employees`) — a list of their own, under `all`. */
      employees: (tenantId: string) => ["tenant", tenantId, "members", "employees"] as const,
    },
    roles: {
      all: (tenantId: string) => ["tenant", tenantId, "roles"] as const,
      list: (tenantId: string) => ["tenant", tenantId, "roles", "list"] as const,
    },
    organisation: {
      all: (tenantId: string) => ["tenant", tenantId, "organisation"] as const,
      current: (tenantId: string) => ["tenant", tenantId, "organisation", "current"] as const,
    },
    /** M5 platform surfaces under Settings: entitlements, API keys, the audit trail, data exports. */
    settings: {
      all: (tenantId: string) => ["tenant", tenantId, "settings"] as const,
      /** Plan, limits vs usage and lifecycle state: the one source for every plan gate (reachable by every member). */
      usage: (tenantId: string) => ["tenant", tenantId, "settings", "usage"] as const,
      /**
       * Setup guides (plans/v2/setup-guides/README.md §2.3): `GET /tenants/current/setup` — when each module's guide was
       * dismissed for the whole organisation, ONE read for all four guides. Under `settings` so the scope the shell
       * already drops on a tenant switch carries it. `useDismissSetup` writes the api's answer into this entry BEFORE it
       * navigates home (a stale null on arrival is a redirect straight back — the Staff guide's finding), then
       * invalidates `["settings"]`; nothing else writes it.
       */
      setup: (tenantId: string) => ["tenant", tenantId, "settings", "setup"] as const,
      apiKeys: (tenantId: string) => ["tenant", tenantId, "settings", "api-keys"] as const,
      audit: (tenantId: string, filters: AuditFilters) =>
        ["tenant", tenantId, "settings", "audit", filters] as const,
      exports: (tenantId: string) => ["tenant", tenantId, "settings", "exports"] as const,
    },
    /** M11 billing (ADR-0015): the subscription the page draws, its orders, and the paged invoices. */
    billing: {
      all: (tenantId: string) => ["tenant", tenantId, "billing"] as const,
      summary: (tenantId: string) => ["tenant", tenantId, "billing", "summary"] as const,
      orders: (tenantId: string, filters: InvoiceFilters) =>
        ["tenant", tenantId, "billing", "orders", filters] as const,
      invoices: (tenantId: string, filters: InvoiceFilters) =>
        ["tenant", tenantId, "billing", "invoices", filters] as const,
    },
    /** M7 outbound webhooks (ADR-0013): endpoints, their delivery log, and the event-type catalogue. */
    webhooks: {
      all: (tenantId: string) => ["tenant", tenantId, "webhooks"] as const,
      list: (tenantId: string) => ["tenant", tenantId, "webhooks", "list"] as const,
      detail: (tenantId: string, endpointId: string) =>
        ["tenant", tenantId, "webhooks", "detail", endpointId] as const,
      deliveries: (tenantId: string, endpointId: string, filters: DeliveryFilters) =>
        ["tenant", tenantId, "webhooks", "deliveries", endpointId, filters] as const,
      delivery: (tenantId: string, deliveryId: string) =>
        ["tenant", tenantId, "webhooks", "delivery", deliveryId] as const,
      eventTypes: (tenantId: string) => ["tenant", tenantId, "webhooks", "event-types"] as const,
    },
  },
} as const;
